import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { usePlatformBrand } from "@/components/app/PlatformBrandProvider";
import { DEFAULT_BRAND } from "@/lib/brandRuntime";

// Brand marks. The SVG assets live in /public so they're served as static
// files with long cache — no JSX re-parse cost, and they're easily overridden
// by a deployer if the brand evolves.
//
// Dark mode uses the pale-pastel variants (#c8fce8 tick, #dae6f7 body) so the
// logo stays legible against the near-black surface.

type Size = "sm" | "md" | "lg";

const WORDMARK_SIZES: Record<Size, string> = {
  sm: "h-5",
  md: "h-7",
  lg: "h-9",
};

const ICON_SIZES: Record<Size, string> = {
  sm: "h-5 w-5",
  md: "h-6 w-6",
  lg: "h-8 w-8",
};

// Text-wordmark sizing for a renamed platform with no uploaded logo.
const WORDMARK_TEXT_SIZES: Record<Size, string> = {
  sm: "text-base",
  md: "text-lg",
  lg: "text-2xl",
};

function useLogoSrc(kind: "logo" | "icon"): string {
  const { resolvedTheme } = useTheme();
  if (kind === "logo") {
    return resolvedTheme === "dark" ? "/logo-white.svg" : "/logo.svg";
  }
  return resolvedTheme === "dark" ? "/icon-white.svg" : "/icon.svg";
}

export function Wordmark({
  size = "md",
  className,
}: {
  size?: Size;
  className?: string;
}) {
  const brand = usePlatformBrand();
  const defaultSrc = useLogoSrc("logo");
  // A super-admin-uploaded logo overrides the built-in wordmark (used as-is on
  // both themes — v1 has no separate dark upload).
  if (brand.logo_url) {
    return (
      <img
        src={brand.logo_url}
        alt={brand.name}
        className={cn(WORDMARK_SIZES[size], "w-auto select-none", className)}
        draggable={false}
      />
    );
  }
  // Renamed platform but no uploaded logo → render the name as a text wordmark
  // so the rebrand is visible everywhere the mark appears (sidebar, login,
  // landing…). An un-rebranded platform keeps the built-in ExamBase SVG.
  if (brand.name && brand.name !== DEFAULT_BRAND.name) {
    return (
      <span
        className={cn(
          WORDMARK_TEXT_SIZES[size],
          "font-semibold tracking-tight text-foreground select-none",
          className,
        )}
      >
        {brand.name}
      </span>
    );
  }
  return (
    <img
      src={defaultSrc}
      alt={brand.name}
      className={cn(WORDMARK_SIZES[size], "w-auto select-none", className)}
      draggable={false}
    />
  );
}

export function BrandIcon({
  size = "md",
  className,
}: {
  size?: Size;
  className?: string;
}) {
  const brand = usePlatformBrand();
  const defaultSrc = useLogoSrc("icon");
  const src = brand.icon_url ?? defaultSrc;
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      className={cn(ICON_SIZES[size], "select-none", className)}
      draggable={false}
    />
  );
}
